import { InfoCircleOutlined } from '@ant-design/icons';
import { Form, Row, Tooltip } from 'antd';
import { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { SwitchableInput } from 'app/components/commons/SwitchableInput/SwitchableInput';
import { LegacyHotelContext } from 'app/context/HotelContext/LegacyHotelContext';
import type { BulkEditFormValues } from 'app/typings/bulkEdit';
import type { ExtranetPackage } from 'app/typings/packages';
import { formatDateWithoutHour } from 'app/utils/dates';
import { computeCrossRate } from 'app/utils/price';

import type {
  FieldsDisplayValues,
  PackageDisplayValues,
  RoomDisplayValues,
} from '../BulkEdit';

type DiscountPriceSwitchProps = {
  openValues: FieldsDisplayValues;
  formValues: BulkEditFormValues;
  initialDiscountPrice?: number;
  toggleSwitch: (
    switchValue: boolean,
    section: keyof PackageDisplayValues | keyof RoomDisplayValues,
    packageId?: number
  ) => void;
  pkg: ExtranetPackage;
};

export const DiscountPriceSwitch = ({
  openValues,
  formValues,
  initialDiscountPrice,
  toggleSwitch,
  pkg,
}: DiscountPriceSwitchProps) => {
  const { currencyInput, currencyFormatter } = useContext(CurrencyContext);

  const { getExperiencesForPackage } = useContext(LegacyHotelContext);

  const isSwitchOn = !!openValues.packageDisplayValues.find(
    (packageValue) => packageValue.packageId === pkg.id
  )?.discountPrice;

  const experiences = getExperiencesForPackage(pkg.id);

  const price = computeCrossRate(
    pkg.id,
    experiences,
    formatDateWithoutHour(formValues.startDate ?? new Date()),
    !!pkg?.dayPackage,
    0
  );

  return (
    <SwitchableInput
      name={`discountPrice-switch-${pkg.id}`}
      value={isSwitchOn}
      onChange={(value: boolean) => {
        toggleSwitch(value, 'discountPrice', pkg.id);
      }}
      title={<h3>{pkg.aliasForHotel}</h3>}
      childNode={
        <>
          <Row align={'middle'} className="switchable-input-name">
            <div>Staycation rate</div>
            {!pkg.dayPackage && (
              <Tooltip
                title={
                  <>
                    <div>The minimum Staycation discount is 18%.</div>
                    <div>
                      Staycation rate = BAR + Package price (
                      {currencyFormatter(price)}) - Staycation discount
                    </div>
                  </>
                }
                placement="top"
                overlayClassName="bulkEdit__tooltip"
              >
                <InfoCircleOutlined color="#BBB" />
              </Tooltip>
            )}
          </Row>
          <div className="inputContainer">
            <Form.Item
              initialValue={initialDiscountPrice}
              name={[`${pkg.id}`, 'discountPrice']}
              dependencies={['bar']}
              rules={[
                {
                  validator() {
                    if (!isSwitchOn) {
                      return Promise.resolve('');
                    }

                    if (
                      formValues?.[pkg.id]?.discountPrice === null ||
                      formValues?.[pkg.id]?.discountPrice === undefined
                    ) {
                      return Promise.reject(
                        'Staycation rate is required if you activate the toggle'
                      );
                    }

                    return Promise.resolve('');
                  },
                },
              ]}
              noStyle={false}
            >
              {currencyInput({
                disabled: !isSwitchOn,
                min: 0,
                placeholder: 'Rate',
                onWheel: (e) => e.currentTarget.blur(),
              })}
            </Form.Item>
          </div>
        </>
      }
    />
  );
};
